import * as Sentry from "@sentry/ember";
import { isMedicUser, isPatientUser, type Metric, type User } from "../types";

/**
 * Sentry 11 applies the scope tags to the errors only. The attribute gives the same value to the spans.
 */
function setScopeTagAndAttribute(
  scope: Sentry.Scope,
  key: string,
  value: string | undefined
) {
  scope.setTag(key, value);

  if (value === undefined) {
    scope.removeAttribute(key);
  } else {
    scope.setAttribute(key, value);
  }
}

// If you update this code, also update back/app/controllers/concerns/semeia_sentry.rb
// to make sure back and front transactions stay in sync.
export function setSentryUser(user: User) {
  const scope = Sentry.getCurrentScope();
  if (isMedicUser(user)) {
    // Sentry tags must be strings, so we stringify the boolean only at this boundary.
    setScopeTagAndAttribute(
      scope,
      "is_super_user",
      user.is_super_user?.toString()
    );

    if (!user.is_super_user) {
      setScopeTagAndAttribute(
        scope,
        "health_centre_uuid",
        user.first_health_centre_uuid
      );
    }
  } else if (isPatientUser(user)) {
    setScopeTagAndAttribute(
      scope,
      "health_centre_uuid",
      user.health_centre_uuid
    );
  }

  scope.setUser({ ...user });
}

const PII_HEADER_SNIPPETS = ["forwarded", "-ip", "remote-", "via", "-user"];

/**
 * Sentry 11 collects user info, cookies and HTTP bodies when `dataCollection` is not set.
 * We handle health data: keep the restrictive Sentry 10 default.
 */
const DATA_COLLECTION: Sentry.BrowserOptions["dataCollection"] = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: PII_HEADER_SNIPPETS },
    response: { deny: PII_HEADER_SNIPPETS }
  },
  httpBodies: [],
  urlQueryParams: { deny: PII_HEADER_SNIPPETS },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false }
};

export function initializeSentry(
  environment: string,
  opts?: Sentry.BrowserOptions
) {
  const sentryConfiguration: Sentry.BrowserOptions = {
    environment,
    normalizeDepth: 11,
    // Only production builds initialize Sentry, see ControlTower.initialize in parog-web/app/app.ts
    tracesSampleRate: 0.2,
    // Sentry 11 adds a stack trace to every message by default, which changes the issue grouping
    attachStacktrace: false,
    // Sentry 11 streams the spans by default. Our self-hosted Sentry (25.12) needs 26.4.2 to read them.
    traceLifecycle: "static",
    dataCollection: DATA_COLLECTION,
    ...(opts || {})
  };

  sentryConfiguration.replaysSessionSampleRate = 1.0;
  sentryConfiguration.replaysOnErrorSampleRate = 1.0;
  const disabledEnvs = ["production", "preproduction"];
  if (!disabledEnvs.includes(environment)) {
    // Enable Sentry Replay, only on non-production environments.
    sentryConfiguration.integrations = [Sentry.replayIntegration()];
    sentryConfiguration.tracesSampleRate = 1.0;
  }

  Sentry.init(sentryConfiguration);
}

export function resetSentryUser() {
  const scope = Sentry.getCurrentScope();
  setScopeTagAndAttribute(scope, "is_super_user", undefined);
  setScopeTagAndAttribute(scope, "health_centre_uuid", undefined);
  scope.setUser(null);
}

/**
 * Actually the metric's tag are not send to Sentry
 */
export function setSentrySpanAttributes(metrics: Metric[]) {
  const activeSpan = Sentry.getActiveSpan();
  metrics.forEach(metric => {
    activeSpan?.setAttribute(metric.name, metric.value);
  });
}

/**
 * Actually the metric's tag are not send to Sentry
 */
export function setSentryTags(metrics: Metric[]) {
  metrics.forEach(metric => {
    Sentry.setTag(metric.name, metric.value);
    Sentry.setAttribute(metric.name, metric.value);
  });
}
